import { LiveTvModel } from '@modules/entertainment/models/live-tv.models.ts';
import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { Button, Menu, MenuItem } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import LiveTvDialogForm from '../live-tv-dialog/live-tv-dialog-form.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import useLiveTvStore from '../../store/live-tv.store.ts';
import { ICONS } from '@shared/components/icons/icon.tsx';
import { LiveTvRowDataModel } from '@modules/entertainment/models/live-tv.models.ts';
import { getLiveTv } from '@shared/api/entertainment-api/live-tv-api.ts';
import { SortOrder, TriggerTypeEnum } from '@shared/models/table.model.ts';
import { useSearchParams } from 'react-router-dom';
import ViewControls from '@components/view-controls/view-controls.tsx';
import countries from '@assets/data/countries.json';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { liveTvColumns } from '@modules/entertainment/pages/live-tv/compnents/live-tv-table/components/columns/columns.tsx';
import { ArrowDownIcon, DownloadIcon } from '@components/icons/svg-icon.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import IptvProvidersDialogContent from '../iptv-providers-dialog-content/iptv-providers-dialog-content.tsx';
import { DialogType } from '@modules/entertainment/utils/live-tv.utils.ts';
import M3e8DialogContent from '@modules/entertainment/pages/live-tv/compnents/m3e8-dialog-content/m3e8-dialog-content.tsx';
import ExtraButtons from './components/extra-buttons/extra-buttons.tsx';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';
import useTableSelection from '@hooks/use-table-selection.ts';
import { getRowIdKey } from '@utils/table-selection.utils.tsx';

const LiveTvTable: React.FC = () => {
  const {
    getCategoryList,
    createRow,
    editRow,
    deleteRow,
    enableChannelsMany,
    disableChannelsMany,
    enableAllChannels,
    disableAllChannels,
    categoryList,
    isLoading
  } = useLiveTvStore();
  const { resetAllSelectedMedia } = useMediaLibraryStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const {
    selectedKeys: selectedRows,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    toggleRow: handleSelectRow,
    togglePage: handleSelectAll,
    clearSelection
  } = useTableSelection<LiveTvModel>({
    tableStore,
    getRowKey: getRowIdKey
  });
  const [view, setView] = useState(ViewControlsEnum.List);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<LiveTvRowDataModel | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [countryList, setCountryList] = useState<
    { title: string; value: string }[]
  >([]);

  const [openDialogType, setOpenDialogType] = useState<DialogType>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');

  const count = selectedRows.length;

  const handleEdit = (row: any, type?: string) => {
    setOpenDialog(true);
    setSelectedItem(row);
  };

  const handleDelete = (row: any) => {
    setShowDeleteDialog(true);
    setSelectedItemId(row?._id || null);
  };

  const handleDeleteConfirm = () => {
    const _id = selectedItemId ? selectedItemId : selectedRows;
    deleteRow({ id: _id, state: tableStore.getState(), handleClose });
  };

  const handleClose = () => {
    setShowDeleteDialog(false);
    resetAllSelectedMedia();
    setOpenDialog(false);
    setSelectedItem(null);
    setSelectedItemId('');
    clearSelection();
  };

  const handleCloseTypeDialog = () => {
    setOpenDialogType(null);
    resetAllSelectedMedia();
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const handleSave = async (data: any) => {
    if (selectedItem?._id) {
      editRow({
        data,
        id: selectedItem?._id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      createRow({
        data,
        state: tableStore.getState(),
        handleClose
      });
    }
  };

  const handleAction = (action: { type: string; value?: string }) => {
    switch (action.type) {
      case ViewControlsEnum.View:
        setView(action.value as ViewControlsEnum);
        break;
      case ViewControlsEnum.Delete:
        handleDelete(selectedRows);
        break;
      case ViewControlsEnum.ImportPlaylist:
        break;
      case ViewControlsEnum.SelectAll:
        break;
      case ViewControlsEnum.Search:
        break;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const columns = liveTvColumns(
    handleEdit,
    handleDelete,
    {
      handleSelectRow,
      handleSelectAll,
      isPageAllSelected,
      isPagePartiallySelected,
      isRowSelected
    },
    categoryList
  );

  const filterCountryList = () => {
    const countryData = countries.map((country) => ({
      title: country.name,
      value: country.name
    }));
    setCountryList(countryData);
  };

  useEffect(() => {
    filterCountryList();
  }, []);

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      getCategoryList();

      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        setOpenDialog(true);
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls
          onAction={handleAction}
          selectedRows={selectedRows}
          showLanguageSelect={false}
          extraButtons={
            <ExtraButtons
              selectedRows={selectedRows}
              onDisableAll={() =>
                disableAllChannels({
                  state: tableStore.getState(),
                  handleClose
                })
              }
              onEnableAll={() =>
                enableAllChannels({
                  state: tableStore.getState(),
                  handleClose
                })
              }
              onDisable={() =>
                disableChannelsMany({
                  ids: selectedRows,
                  state: tableStore.getState(),
                  handleClose
                })
              }
              onEnable={() =>
                enableChannelsMany({
                  ids: selectedRows,
                  state: tableStore.getState(),
                  handleClose
                })
              }
            />
          }
        />

        <div className="flex gap-2">
          <div>
            <Button
              variant="outlined"
              startIcon={
                <DownloadIcon
                  className="w-4.5! h-4.5!"
                  stroke={'var(--dark-2)'}
                />
              }
              endIcon={
                <ArrowDownIcon
                  sx={{
                    width: '18px !important',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}
                />
              }
              onClick={handleClick}
            >
              {t('import')}
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              sx={{ minWidth: 200 }}
            >
              <MenuItem
                onClick={() => {
                  setOpenDialogType('m3u8');
                  handleCloseMenu();
                }}
              >
                {t('m3u8_playlist')}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOpenDialogType('iptvProvider');
                  handleCloseMenu();
                }}
              >
                {t('iptv_providers')}
              </MenuItem>
            </Menu>
          </div>

          <Button
            className="min-w-42.5!"
            variant="contained"
            color="primary"
            startIcon={ICONS.plusIcon}
            onClick={() => handleEdit(selectedItem)}
          >
            {t('add')}
          </Button>
        </div>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        {view === ViewControlsEnum.List && (
          <DynamicTable
            tableStore={tableStore}
            apiFunction={getLiveTv}
            columns={columns}
            onTriggerAction={handleTableTrigger}
            tableKey="entertainment-live-tv-table"
            initialState={{
              sort: ['createdAt', SortOrder.Descending]
            }}
          />
        )}
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          title={t('entertainment_live_tv_title')}
          size="lg"
          content={
            <LiveTvDialogForm
              selectedItem={selectedItem}
              onSave={handleSave}
              categoryList={categoryList}
              countryList={countryList}
            />
          }
          onClose={handleClose}
        />

        <AppDialog
          disableBackdropClick
          open={openDialogType !== null}
          titleAlign="center"
          title={t('entertainment_live_tv_import_channels_title')}
          size={isTabletScreen ? 'sm' : 'md'}
          content={
            openDialogType === 'iptvProvider' ? (
              <IptvProvidersDialogContent
                state={tableStore.getState()}
                onClose={handleCloseTypeDialog}
              />
            ) : (
              <M3e8DialogContent
                onClose={handleCloseTypeDialog}
                state={tableStore.getState()}
              />
            )
          }
          onClose={handleCloseTypeDialog}
        />

        <DeleteDialog
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          isLoading={isLoading()}
          title={
            selectedItemId
              ? t('delete_popup_title')
              : t('delete_popup_title_many_items', { count })
          }
        />
      </div>
    </div>
  );
};

export default LiveTvTable;
